/* --------------------------------- IMPORTS -------------------------------- */

import { FC, useState } from 'react';
import { KTSVG } from '../../../../../_metronic/helpers';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import clsx from 'clsx';
import { AddCourseFormDetails } from '../../../../types';
import { useAppDispatch } from '../../../../redux/hooks';
import { addCourse, TranscriptState } from '../../../../redux/slices/UserSlice';
import { closeModalById, removeEmpty } from '../../../../helpers';

/* -------------------------------------------------------------------------- */

/* --------------------------- FORM INITIAL VALUES -------------------------- */

const initialValues = {
    pfn_name: '',
    pfn_sponsor: '',
    pfn_location: '',
    pfn_speaker: '',
    pfn_startdate1: '',
    pfn_enddate1: '',
    pfn_creditssubmitted: '',
};

/* -------------------------------------------------------------------------- */

/* ---------------------------------- TYPES --------------------------------- */

interface Props {
    transcript: TranscriptState;
}

/* -------------------------------------------------------------------------- */

/* ------------------------------- FORM SCHEMA ------------------------------ */

const validationSchema = Yup.object().shape({
    pfn_name: Yup.string().required('Title is required'),
    pfn_sponsor: Yup.string(),
    pfn_location: Yup.string(),
    pfn_speaker: Yup.string().required('Speaker is required'),
    pfn_startdate1: Yup.string().required('Start date is required'),
    pfn_enddate1: Yup.string().required('End date is required'),
    pfn_creditssubmitted: Yup.string()
        .required('Credits is required')
        .test('checkMaxMin', 'Must be no within the range of 0 - 2000', async (val) => {
            return Number(val) <= 2000 && Number(val) >= 0;
        }),
});

/* -------------------------------------------------------------------------- */

/* -------------------------------- COMPONENT ------------------------------- */

const AddCourseBtn: FC<Props> = ({ transcript }) => {
    const [loading, setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState<string | null>(null);
    const dispatch = useAppDispatch();

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            try {
                setErrMsg(null);
                setLoading(true);
                if (!transcript.id) {
                    throw new Error('Transcript id does not exist');
                }
                const response = await dispatch(
                    addCourse({
                        pfn_cetranscriptid: transcript.id,
                        details: removeEmpty(values) as AddCourseFormDetails,
                    }),
                );
                if (response) {
                    if (!response.payload?.success) {
                        if (response.payload?.message) {
                            setErrMsg(response.payload.message);
                        } else {
                            setErrMsg('An error has occured, please try again later');
                        }
                    } else {
                        closeModalById('kt_modal_add_course');
                    }
                } else {
                    setErrMsg('An error has occured, please try again later');
                }

                setLoading(false);
            } catch (err) {
                if (err instanceof Error) {
                    setErrMsg(err.message);
                } else {
                    setErrMsg('An error has occered, please try again later');
                }
                setLoading(false);
            }
        },
    });

    const GenerateAddCourseModal = () => {
        return (
            <div className='modal fade' tabIndex={-1} id='kt_modal_add_course'>
                <div className='modal-dialog'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h5 className='modal-title'>Add Course</h5>
                            <div
                                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                                data-bs-dismiss='modal'
                                aria-label='Close'
                            >
                                <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
                            </div>
                        </div>
                        <div className='modal-body'>{GenerateAddCourseForm()}</div>
                    </div>
                </div>
            </div>
        );
    };

    const GenerateAddCourseForm = () => {
        return (
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate id='addCourseForm'>
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fs-6 fw-bolder text-dark required'>Course Name</label>
                    <input
                        {...formik.getFieldProps('pfn_name')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_name && formik.errors.pfn_name,
                        })}
                        name='pfn_name'
                        autoComplete='off'
                    />
                    {formik.touched.pfn_name && formik.errors.pfn_name && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                                {formik.errors.pfn_name}
                            </span>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fs-6 fw-bolder text-dark'>Session Host/Sponsor</label>
                    <input
                        {...formik.getFieldProps('pfn_sponsor')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_sponsor && formik.errors.pfn_sponsor,
                        })}
                        name='pfn_sponsor'
                        autoComplete='off'
                    />
                    {formik.touched.pfn_sponsor && formik.errors.pfn_sponsor && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                                {formik.errors.pfn_sponsor}
                            </span>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fs-6 fw-bolder text-dark'>Location</label>
                    <input
                        {...formik.getFieldProps('pfn_location')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_location && formik.errors.pfn_location,
                        })}
                        name='pfn_location'
                        autoComplete='off'
                    />
                    {formik.touched.pfn_location && formik.errors.pfn_location && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                                {formik.errors.pfn_location}
                            </span>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fs-6 fw-bolder text-dark required'>Speaker Name</label>
                    <input
                        {...formik.getFieldProps('pfn_speaker')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_speaker && formik.errors.pfn_speaker,
                        })}
                        name='pfn_speaker'
                        autoComplete='off'
                    />
                    {formik.touched.pfn_speaker && formik.errors.pfn_speaker && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                                {formik.errors.pfn_speaker}
                            </span>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fw-bolder text-dark fs-6 required'>Start Date</label>

                    <input
                        type='date'
                        min='1900-01-01'
                        max='9999-12-31'
                        autoComplete='off'
                        {...formik.getFieldProps('pfn_startdate1')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_startdate1 && formik.errors.pfn_startdate1,
                        })}
                    />
                    {formik.touched.pfn_startdate1 && formik.errors.pfn_startdate1 && (
                        <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                                <span role='alert' className='text-danger'>
                                    {formik.errors.pfn_startdate1}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fw-bolder text-dark fs-6 required'>End Date</label>

                    <input
                        type='date'
                        min='1900-01-01'
                        max='9999-12-31'
                        autoComplete='off'
                        {...formik.getFieldProps('pfn_enddate1')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_enddate1 && formik.errors.pfn_enddate1,
                        })}
                    />
                    {formik.touched.pfn_enddate1 && formik.errors.pfn_enddate1 && (
                        <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                                <span role='alert' className='text-danger'>
                                    {formik.errors.pfn_enddate1}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {/* End Form Row */}
                {/* Start Form Row */}
                <div className='fv-row mb-5'>
                    <label className='form-label fs-6 fw-bolder text-dark required'>Number of Credits</label>
                    <input
                        {...formik.getFieldProps('pfn_creditssubmitted')}
                        className={clsx('form-control form-control-lg form-control-solid', {
                            'is-invalid': formik.touched.pfn_creditssubmitted && formik.errors.pfn_creditssubmitted,
                        })}
                        type='number'
                        max='2000'
                        min='0'
                        name='pfn_creditssubmitted'
                        autoComplete='off'
                    />
                    {formik.touched.pfn_creditssubmitted && formik.errors.pfn_creditssubmitted && (
                        <div className='fv-plugins-message-container'>
                            <span role='alert' className='text-danger'>
                                {formik.errors.pfn_creditssubmitted}
                            </span>
                        </div>
                    )}
                </div>
                {/* End Form Row */}

                <div className='text-muted mb-5'>
                    <b>Note:</b> You will be prompted to upload a supporting document after this course has been added
                    to your transcript. This document can be uploaded on the transcripts page.
                </div>
                <div className='text-muted mb-5'>
                    <b>Note:</b> If you attended a multi-day or multi-session conference, you only need to create a
                    single entry for the entire program. You will need to provide a list of the specific sessions you
                    attended along with your certificate of completion.
                </div>

                <div className='d-flex align-items-center mb-10'>
                    <div className='border-bottom border-gray-300 mw-50 w-100'></div>
                    <div className='border-bottom border-gray-300 mw-50 w-100'></div>
                </div>

                <div className='fv-row'>
                    <div className='d-flex justify-content-end'>
                        <button type='button' className='btn btn-light me-3' data-bs-dismiss='modal'>
                            Cancel
                        </button>

                        <button type='submit' className='btn btn-primary' disabled={formik.isSubmitting || loading}>
                            {!loading && <span className='indicator-label'>Submit</span>}
                            {loading && (
                                <span className='indicator-progress' style={{ display: 'block' }}>
                                    Please wait...
                                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                {errMsg && <div className='text-danger text-center mt-7'>{errMsg}</div>}
            </form>
        );
    };

    return (
        <>
            <button
                className='btn btn-primary me-5'
                data-bs-toggle='modal'
                data-bs-target={`#kt_modal_add_course`}
                onClick={() => {
                    formik.resetForm();
                    setErrMsg(null);
                }}
            >
                Add Course
            </button>

            {GenerateAddCourseModal()}
        </>
    );
};

/* -------------------------------------------------------------------------- */

/* --------------------------------- EXPORTS -------------------------------- */

export { AddCourseBtn };

/* -------------------------------------------------------------------------- */
